/**
 * @fileoverview Admin Clients Page Component
 *
 * Displays all clients with search, filtering, and card/list view toggle.
 * Primary admin interface for client management and navigation to client details.
 *
 * Features:
 * - Search by client name, email, or phone
 * - Filter by client status and project status
 * - Filter by project existence (has project / no project)
 * - Toggle between card and list views
 * - Click-through to individual client detail pages
 * - Mobile-optimized for field use
 *
 * @requires react
 * @requires react-router-dom
 * @requires react-icons/fa
 * @version 1.0.0
 */

import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  FaSearch,
  FaFilter,
  FaTh,
  FaList,
  FaChevronDown,
  FaChevronUp,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaSpinner,
  FaExclamationTriangle,
  FaUserFriends,
  FaExternalLinkAlt,
} from "react-icons/fa";
import {
  useAdminClients,
  useCreateClient,
  useUpdateClient,
} from "../../hooks/useAdminClients";
import useUserPreferences from "../../hooks/useUserPreferences";
import ClientModal from "../../components/admin/ClientModal";
import ClientInfoModal from "../../components/admin/ClientInfoModal";
import AdminPageBanner from "../../components/admin/AdminPageBanner";
import { formatDateET } from "../../utils/dateUtils";
import { formatPhoneDisplay } from "../../utils/phoneUtils";
import "./AdminClients.css";

/* ============================================
   CONSTANTS
   ============================================ */

/**
 * Client status options for filtering
 * Note: "Lead Lost" is excluded from default view but can be shown via filter
 * @constant {Array<Object>}
 */
const CLIENT_STATUSES = [
  { value: "all", label: "All Current Clients" },
  { value: "Lead", label: "Lead" },
  { value: "Active", label: "Active" },
  { value: "In Warranty", label: "In Warranty" },
  { value: "Former Client", label: "Former Client" },
  { value: "Lead Lost", label: "Lead Lost" },
];

/**
 * Client status sort order for organizing display
 * @constant {Object}
 */
const CLIENT_STATUS_ORDER = {
  Active: 1,
  Lead: 2,
  "In Warranty": 3,
  "Former Client": 4,
  "Lead Lost": 5,
};

/**
 * Project existence filter options
 * @constant {Array<Object>}
 */
const PROJECT_FILTERS = [
  { value: "all", label: "All Clients" },
  { value: "hasProject", label: "Has Project" },
  { value: "noProject", label: "No Project" },
];

/**
 * Project status options for filtering (when hasProject)
 * @constant {Array<Object>}
 */
const PROJECT_STATUSES = [
  { value: "all", label: "All Project Statuses" },
  { value: "Pre-Construction", label: "Pre-Construction" },
  { value: "Active Construction", label: "Active Construction" },
  { value: "Construction Delayed", label: "Construction Delayed" },
  {
    value: "Construction Completed / Under Warranty",
    label: "Under Warranty",
  },
];

/**
 * Returns client status header color class (for card header background)
 * @param {string} status - Client status
 * @returns {string} CSS class name
 */
const getClientStatusColor = (status) => {
  switch (status) {
    case "Active":
      return "admin-clients-status-active";
    case "In Warranty":
      return "admin-clients-status-warranty";
    case "Lead":
      return "admin-clients-status-lead";
    case "Former Client":
      return "admin-clients-status-former";
    case "Lead Lost":
      return "admin-clients-status-lost";
    default:
      return "admin-clients-status-default";
  }
};

/**
 * Returns project status pill color class (for pill in card header)
 * @param {string} status - Project status
 * @returns {string} CSS class name
 */
const getProjectStatusColor = (status) => {
  switch (status) {
    case "Active Construction":
      return "admin-clients-pill-active";
    case "Pre-Construction":
      return "admin-clients-pill-pre";
    case "Construction Delayed":
      return "admin-clients-pill-delayed";
    case "Construction Completed / Under Warranty":
      return "admin-clients-pill-warranty";
    default:
      return "admin-clients-pill-default";
  }
};

/* ============================================
   SUB-COMPONENTS
   ============================================ */

/**
 * Search and filter controls bar
 * @param {Object} props - Component props
 */
const SearchFilterBar = ({
  searchTerm,
  onSearchChange,
  showFilters,
  onToggleFilters,
  viewMode,
  onViewModeChange,
  activeFilterCount,
}) => (
  <div className="admin-clients-controls">
    {/* Search Input */}
    <div className="admin-clients-search">
      <FaSearch className="admin-clients-search-icon" />
      <input
        type="text"
        placeholder="Search clients, emails, or phone numbers..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        className="admin-clients-search-input"
      />
    </div>

    {/* Control Buttons */}
    <div className="admin-clients-control-buttons">
      {/* Filter Toggle */}
      <button
        className={`admin-clients-filter-btn ${showFilters ? "active" : ""}`}
        onClick={onToggleFilters}
      >
        <FaFilter />
        <span>Filters</span>
        {activeFilterCount > 0 && (
          <span className="admin-clients-filter-count">
            {activeFilterCount}
          </span>
        )}
        {showFilters ? <FaChevronUp /> : <FaChevronDown />}
      </button>

      {/* View Mode Toggle */}
      <div className="admin-clients-view-toggle">
        <button
          className={`admin-clients-view-btn ${
            viewMode === "card" ? "active" : ""
          }`}
          onClick={() => onViewModeChange("card")}
        >
          <FaTh /> <span className="hide-sm">Card View</span>
        </button>
        <button
          className={`admin-clients-view-btn ${
            viewMode === "list" ? "active" : ""
          }`}
          onClick={() => onViewModeChange("list")}
        >
          <FaList />
          <span className="hide-sm">List View</span>
        </button>
      </div>
    </div>
  </div>
);

/**
 * Collapsible filter panel
 * @param {Object} props - Component props
 */
const FilterPanel = ({
  filters,
  onFilterChange,
  onClearFilters,
  isVisible,
}) => {
  if (!isVisible) return null;

  return (
    <div className="admin-clients-filters">
      <div className="admin-clients-filter-row">
        {/* Client Status Filter */}
        <div className="admin-clients-filter-group">
          <label className="admin-clients-filter-label">Client Status</label>
          <select
            value={filters.status}
            onChange={(e) => onFilterChange("status", e.target.value)}
            className="admin-clients-filter-select"
          >
            {CLIENT_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        {/* Project Existence Filter */}
        <div className="admin-clients-filter-group">
          <label className="admin-clients-filter-label">Project Status</label>
          <select
            value={filters.hasProject}
            onChange={(e) => onFilterChange("hasProject", e.target.value)}
            className="admin-clients-filter-select"
          >
            {PROJECT_FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
        </div>

        {/* Project Status Filter (only shown if hasProject) */}
        {filters.hasProject === "hasProject" && (
          <div className="admin-clients-filter-group">
            <label className="admin-clients-filter-label">Project Status</label>
            <select
              value={filters.projectStatus}
              onChange={(e) => onFilterChange("projectStatus", e.target.value)}
              className="admin-clients-filter-select"
            >
              {PROJECT_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Clear Filters Button */}
        <button
          className="admin-clients-clear-filters"
          onClick={onClearFilters}
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

/**
 * Client card for grid view
 * @param {Object} props - Component props
 */
const ClientCard = ({ client, onClick }) => (
  <div className="admin-clients-card" onClick={() => onClick(client.id)}>
    {/* Card Header - Color coded by client status */}
    <div
      className={`admin-clients-card-header ${getClientStatusColor(
        client.clientStatus,
      )}`}
    >
      <div className="admin-clients-card-status-label">
        <FaUser />
        <span>{client.clientStatus}</span>
      </div>
      <span
        className={`admin-clients-card-project-pill ${
          client.projectName
            ? getProjectStatusColor(client.projectStatus)
            : "admin-clients-pill-none"
        }`}
      >
        {client.projectName || "No Project Assigned"}
      </span>
    </div>

    {/* Card Body */}
    <div className="admin-clients-card-body">
      <h3 className="admin-clients-card-title">{client.clientName}</h3>

      <div className="admin-clients-card-info">
        {/* Email */}
        <div className="admin-clients-card-row">
          <FaEnvelope className="admin-clients-card-icon" />
          <span>{client.email || "No email"}</span>
        </div>

        {/* Phone */}
        <div className="admin-clients-card-row">
          <FaPhone className="admin-clients-card-icon" />
          <span>{formatPhoneDisplay(client.phone) || "No phone"}</span>
        </div>

        {/* Mailing Address */}
        <div className="admin-clients-card-row">
          <FaMapMarkerAlt className="admin-clients-card-icon" />
          <span className="admin-clients-card-address">
            {client.mailingAddress || "No mailing address"}
          </span>
        </div>
      </div>
    </div>

    {/* Card Footer - Notes */}
    <div className="admin-clients-card-footer">
      <div className="admin-clients-card-notes">
        <span className="admin-clients-notes-clamp">
          {client.notes?.trim() ? (
            <>
              <span className="admin-clients-notes-label">
                Internal Notes:{" "}
              </span>
              <span className="admin-clients-notes-text">{client.notes}</span>
            </>
          ) : (
            <span className="admin-clients-notes-text is-empty">
              No client notes
            </span>
          )}
        </span>
      </div>
    </div>
  </div>
);

/**
 * Client row for list view
 * @param {Object} props - Component props
 */
const ClientRow = ({ client, onClick }) => (
  <tr className="admin-clients-row" onClick={() => onClick(client.id)}>
    <td className="admin-clients-cell-name">
      <div className="admin-clients-name-cell">
        <span
          className={`admin-clients-type-icon ${
            client.clientStatus === "Active"
              ? "status-active"
              : client.clientStatus === "In Warranty"
                ? "status-warranty"
                : client.clientStatus === "Lead"
                  ? "status-lead"
                  : client.clientStatus === "Former Client"
                    ? "status-former"
                    : client.clientStatus === "Lead Lost"
                      ? "status-lost"
                      : "status-default"
          }`}
        >
          <FaUser />
        </span>
        <div>
          <span className="admin-clients-row-title">{client.clientName}</span>
          <span className="admin-clients-row-subtitle">{client.email}</span>
        </div>
      </div>
    </td>
    <td className="admin-clients-cell hide-md">
      {formatPhoneDisplay(client.phone) || "—"}
    </td>
    <td className="admin-clients-cell">
      <span
        className={`admin-clients-status-badge ${
          client.clientStatus === "Active"
            ? "badge-active"
            : client.clientStatus === "In Warranty"
              ? "badge-warranty"
              : client.clientStatus === "Lead"
                ? "badge-lead"
                : client.clientStatus === "Former Client"
                  ? "badge-former"
                  : client.clientStatus === "Lead Lost"
                    ? "badge-lost"
                    : "badge-default"
        }`}
      >
        {client.clientStatus}
      </span>
    </td>
    <td className="admin-clients-cell">{client.projectName || "—"}</td>
    <td className="admin-clients-cell">
      {client.recordCreated ? formatDateET(client.recordCreated) : "—"}
    </td>
  </tr>
);

/**
 * Mobile accordion item for list view
 * Expandable row showing full client details
 * @param {Object} props - Component props
 */
const MobileAccordionItem = ({ client, isExpanded, onToggle, onNavigate }) => (
  <div className="admin-clients-accordion-item">
    {/* Accordion Header - Always visible */}
    <div className="admin-clients-accordion-header" onClick={onToggle}>
      <span
        className={`admin-clients-type-icon ${
          client.clientStatus === "Active"
            ? "status-active"
            : client.clientStatus === "In Warranty"
              ? "status-warranty"
              : client.clientStatus === "Lead"
                ? "status-lead"
                : client.clientStatus === "Former Client"
                  ? "status-former"
                  : client.clientStatus === "Lead Lost"
                    ? "status-lost"
                    : "status-default"
        }`}
      >
        <FaUser />
      </span>
      <div className="admin-clients-accordion-title">
        <span className="admin-clients-accordion-name">
          {client.clientName}
        </span>
        <span className="admin-clients-accordion-subtitle">
          {client.projectName || "No project assigned"}
        </span>
      </div>
      <span
        className={`admin-clients-accordion-chevron ${
          isExpanded ? "expanded" : ""
        }`}
      >
        <FaChevronDown />
      </span>
    </div>

    {/* Accordion Content - Shown when expanded */}
    {isExpanded && (
      <div className="admin-clients-accordion-content">
        {/* Go to Client Button */}
        <button
          className="admin-clients-accordion-goto"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(client.id);
          }}
        >
          <span>View More Information</span>
          <FaExternalLinkAlt />
        </button>

        {/* Status Pills Row */}
        <div className="admin-clients-accordion-row">
          <span
            className={`admin-clients-status-badge ${
              client.clientStatus === "Active"
                ? "badge-active"
                : client.clientStatus === "In Warranty"
                  ? "badge-warranty"
                  : client.clientStatus === "Lead"
                    ? "badge-lead"
                    : client.clientStatus === "Former Client"
                      ? "badge-former"
                      : client.clientStatus === "Lead Lost"
                        ? "badge-lost"
                        : "badge-default"
            }`}
          >
            {client.clientStatus}
          </span>
          {client.projectStatus && (
            <span
              className={`admin-clients-project-badge ${
                client.projectStatus === "Active Construction"
                  ? "badge-active"
                  : client.projectStatus === "Pre-Construction"
                    ? "badge-pre"
                    : client.projectStatus === "Construction Delayed"
                      ? "badge-delayed"
                      : client.projectStatus ===
                          "Construction Completed / Under Warranty"
                        ? "badge-warranty"
                        : "badge-default"
              }`}
            >
              {client.projectStatus}
            </span>
          )}
        </div>

        {/* Details */}
        <div className="admin-clients-accordion-details">
          {/* Email */}
          <div className="admin-clients-accordion-detail">
            <FaEnvelope className="admin-clients-accordion-icon" />
            <span>{client.email || "No email"}</span>
          </div>

          {/* Phone */}
          <div className="admin-clients-accordion-detail">
            <FaPhone className="admin-clients-accordion-icon" />
            <span>{formatPhoneDisplay(client.phone) || "No phone"}</span>
          </div>

          {/* Mailing Address */}
          <div className="admin-clients-accordion-detail">
            <FaMapMarkerAlt className="admin-clients-accordion-icon" />
            <span>{client.mailingAddress || "No mailing address"}</span>
          </div>
        </div>
        {/* Card Footer - Notes */}
        {client.notes && (
          <div className="admin-clients-card-footer-accordion">
            <div className="admin-clients-card-notes">
              <span className="admin-clients-notes-clamp">
                <span className="admin-clients-notes-label">Notes: </span>
                <span className="admin-clients-notes-text">{client.notes}</span>
              </span>
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

/* ============================================
   MAIN COMPONENT
   ============================================ */

/**
 * Admin Clients Page Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.userData - Authenticated user data
 * @returns {JSX.Element} Admin clients page
 */
const AdminClients = ({ userData, showTestAccounts }) => {
  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("card");
  const [expandedAccordionId, setExpandedAccordionId] = useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    hasProject: "all",
    projectStatus: "all",
  });
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [sortBy, setSortBy] = useState("recordCreated");
  const [sortDirection, setSortDirection] = useState("desc");

  // Seed view/sort from saved user preferences once on load. The
  // `preferencesLoaded` ref prevents later hook re-renders from
  // overwriting in-session user changes.
  const { preferences, isLoading: preferencesLoading } = useUserPreferences();
  const preferencesLoaded = useRef(false);

  useEffect(() => {
    if (preferencesLoaded.current) return;
    if (preferencesLoading) return;
    setViewMode(preferences.clientsDefaultView);
    setSortBy(preferences.clientsDefaultSort);
    setSortDirection(preferences.clientsSortDirection);
    preferencesLoaded.current = true;
  }, [
    preferencesLoading,
    preferences.clientsDefaultView,
    preferences.clientsDefaultSort,
    preferences.clientsSortDirection,
  ]);

  // Fetch clients from Airtable via Firebase
  const {
    data: clients,
    isLoading,
    error,
  } = useAdminClients({
    status: filters.status,
    projectStatus: filters.projectStatus,
    showTestAccounts,
    hasProject: filters.hasProject,
  });

  // Client Modal state
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();

  /**
   * Handle filter change
   * @param {string} filterKey - Filter key to update
   * @param {string} value - New filter value
   */
  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setFilters({
      status: "all",
      hasProject: "all",
      projectStatus: "all",
    });
  };

  // Open modal
  const handleClientClick = (clientId) => {
    setSelectedClientId(clientId);
    setShowClientModal(true);
  };

  /**
   * Count active filters
   * @returns {number} Number of active filters
   */
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter((v) => v !== "all").length;
  }, [filters]);

  /**
   * Filter and search clients
   * Default: Exclude "Lead Lost" unless specifically filtered
   * Sort: By client status order, then alphabetically by name
   */
  const filteredClients = useMemo(() => {
    if (!clients) return [];

    const filtered = clients.filter((client) => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        client.clientName?.toLowerCase().includes(searchLower) ||
        client.email?.toLowerCase().includes(searchLower) ||
        client.phone?.toLowerCase().includes(searchLower) ||
        client.secondaryEmail?.toLowerCase().includes(searchLower);

      // Status filter - Exclude "Lead Lost" by default when "all" is selected
      let matchesStatus;
      if (filters.status === "all") {
        matchesStatus = client.clientStatus !== "Lead Lost";
      } else {
        matchesStatus = client.clientStatus === filters.status;
      }

      // Project existence filter (already handled by backend, but double check)
      const matchesProject =
        filters.hasProject === "all" ||
        (filters.hasProject === "hasProject" && client.projectAddress) ||
        (filters.hasProject === "noProject" && !client.projectAddress);

      // Project status filter (only if hasProject)
      const matchesProjectStatus =
        filters.projectStatus === "all" ||
        !client.projectStatus ||
        client.projectStatus === filters.projectStatus;

      return (
        matchesSearch && matchesStatus && matchesProject && matchesProjectStatus
      );
    });

    const sortKeyMap = {
      clientName: (c) => {
        const parts = (c.clientName || "").trim().split(/\s+/);
        return (parts[parts.length - 1] || "").toLowerCase();
      },
      clientStatus: (c) =>
        CLIENT_STATUS_ORDER[c.clientStatus] ?? 999,
      projectName: (c) => (c.projectName || "").toLowerCase(),
      recordCreated: (c) =>
        c.recordCreated ? new Date(c.recordCreated).getTime() : 0,
    };
    const getKey = sortKeyMap[sortBy] || sortKeyMap.recordCreated;
    const dir = sortDirection === "desc" ? -1 : 1;

    return [...filtered].sort((a, b) => {
      const ka = getKey(a);
      const kb = getKey(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      const na = (a.clientName || "").toLowerCase();
      const nb = (b.clientName || "").toLowerCase();
      return na.localeCompare(nb);
    });
  }, [clients, searchTerm, filters, sortBy, sortDirection]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDirection(key === "recordCreated" ? "desc" : "asc");
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="loading-container">
        <FaSpinner className="admin-dash-spinner" />
        <span className="loading-text">Loading clients...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="admin-clients-page">
        <div className="state-container">
          <FaExclamationTriangle className="state-icon" />
          <h2 className="state-title">Error Loading Clients</h2>
          <p className="state-message">
            {error.message || "Unable to load clients. Please try again."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-clients-page">
      {/* Page Banner */}
      <AdminPageBanner
        title="Clients"
        subtitle="Manage homeowner accounts and contact info"
        count={filteredClients.length}
        countLabel="Active Clients"
        addButton={{
          label: "New Client",
          onClick: () => setShowCreateClientModal(true),
        }}
      />

      {/* Search and Filter Controls */}
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        activeFilterCount={activeFilterCount}
      />

      {/* Filter Panel */}
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        isVisible={showFilters}
      />

      {/* Clients Display */}
      {filteredClients.length === 0 ? (
        <div className="state-container">
          <FaUserFriends className="state-icon" />
          <h2 className="state-title">No Clients Found</h2>
          <p className="state-message">
            {searchTerm || activeFilterCount > 0
              ? "Try adjusting your search or filters."
              : "No clients have been created yet."}
          </p>
        </div>
      ) : viewMode === "card" ? (
        /* Card View */
        <div className="admin-clients-grid">
          {filteredClients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onClick={handleClientClick}
            />
          ))}
        </div>
      ) : (
        /* List View */
        <>
          {/* Desktop/Tablet Table View */}
          <div className="admin-clients-table-container hide-mobile">
            <table className="admin-clients-table">
              <thead>
                <tr>
                  {[
                    { key: "clientName", label: "Client", className: "" },
                    { key: null, label: "Phone", className: "hide-md" },
                    {
                      key: "clientStatus",
                      label: "Client Status",
                      className: "",
                    },
                    { key: "projectName", label: "Project", className: "" },
                    {
                      key: "recordCreated",
                      label: "Date Created",
                      className: "",
                    },
                  ].map((col) => {
                    if (!col.key) {
                      return (
                        <th key={col.label} className={col.className}>
                          {col.label}
                        </th>
                      );
                    }
                    const isActive = sortBy === col.key;
                    const classes = [
                      "admin-clients-sortable-th",
                      col.className,
                      isActive ? "active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <th
                        key={col.key}
                        className={classes}
                        onClick={() => handleSort(col.key)}
                      >
                        <span className="admin-clients-sortable-th-inner">
                          <span>{col.label}</span>
                          {isActive && (
                            <span className="admin-clients-sort-chevron">
                              {sortDirection === "asc" ? (
                                <FaChevronDown />
                              ) : (
                                <FaChevronUp />
                              )}
                            </span>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <ClientRow
                    key={client.id}
                    client={client}
                    onClick={handleClientClick}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Accordion View */}
          <div className="admin-clients-accordion show-mobile">
            {filteredClients.map((client) => (
              <MobileAccordionItem
                key={client.id}
                client={client}
                isExpanded={expandedAccordionId === client.id}
                onToggle={() =>
                  setExpandedAccordionId(
                    expandedAccordionId === client.id ? null : client.id,
                  )
                }
                onNavigate={handleClientClick}
              />
            ))}
          </div>
        </>
      )}

      {showClientModal && selectedClientId && (
        <ClientInfoModal
          clientId={selectedClientId}
          isOpen={showClientModal}
          onClose={() => {
            setShowClientModal(false);
            setSelectedClientId(null);
          }}
        />
      )}

      {/* Client Modal — Full Add mode */}
      <ClientModal
        isOpen={showCreateClientModal}
        onClose={() => setShowCreateClientModal(false)}
        mode="full"
        mutations={{
          createClient: createClientMutation,
          updateClient: updateClientMutation,
        }}
      />
    </div>
  );
};

export default AdminClients;
